import { ICON_NAMES } from '../../../../../constants'

export const URGENT_FILTER_KEY = 'urgent'

const FALLBACK_STATUS_STYLE = {
  color: 'var(--text-color-subtle)',
  icon: ICON_NAMES.radioUnchecked,
  order: 0
}

const BUILT_IN_STATUS_STYLES = {
  'On Track': { color: 'var(--color-growth-500)', icon: ICON_NAMES.checkCircle, order: 30 },
  'In Progress': { color: 'var(--color-warning-500)', icon: ICON_NAMES.clock, order: 20 },
  'Not Started': { color: 'var(--color-neutral-400)', icon: ICON_NAMES.radioUnchecked, order: 10 },
  Complete: { color: 'var(--color-growth-500)', icon: ICON_NAMES.checkCircle, order: 40 },
  'Not Now': { color: 'var(--color-neutral-400)', icon: ICON_NAMES.block, order: 5 }
}

// No `order` here — the urgent pill is appended after the rest of the pills
// have been sorted, so it always sits at the end of the row.
export const URGENT_PILL_STYLE = {
  color: 'var(--color-decline-500)',
  icon: ICON_NAMES.warning
}

export function getStatusStyle (statusString, overrides) {
  if (!statusString) return FALLBACK_STATUS_STYLE
  return overrides?.[statusString] ?? BUILT_IN_STATUS_STYLES[statusString] ?? FALLBACK_STATUS_STYLE
}

// Walks every category and item under a plan. The plan itself is intentionally
// excluded from filter pills/counts since it is always rendered as the
// container ("The Plan") rather than a filterable row.
function walkNodes (plan, visit) {
  if (!plan) return
  for (const cat of plan.data ?? []) {
    visit(cat)
    for (const item of cat.data ?? []) {
      visit(item)
    }
  }
}

export function deriveFilterPills (plan, statusOverrides) {
  if (!plan) return []
  const counts = new Map()
  let urgentCount = 0
  walkNodes(plan, (node) => {
    if (node?.status) counts.set(node.status, (counts.get(node.status) ?? 0) + 1)
    if (node?.metadata?.needsAttention) urgentCount += 1
  })
  const pills = Array.from(counts.entries()).map(([status, count]) => ({
    key: status,
    label: status,
    count,
    style: getStatusStyle(status, statusOverrides)
  }))
  pills.sort((a, b) => (b.style.order ?? 0) - (a.style.order ?? 0))
  if (urgentCount > 0) {
    pills.push({
      key: URGENT_FILTER_KEY,
      label: 'Urgent',
      count: urgentCount,
      style: URGENT_PILL_STYLE
    })
  }
  return pills
}

export function countTotalItems (plan) {
  if (!plan) return 0
  let total = 0
  walkNodes(plan, () => { total += 1 })
  return total
}

function nodeMatchesFilter (node, currentFilter) {
  if (!currentFilter) return false
  if (currentFilter === URGENT_FILTER_KEY) return !!node?.metadata?.needsAttention
  return node?.status === currentFilter
}

// A category is kept when its own status matches the filter (in which case
// all of its items are retained) OR when at least one of its items matches
// (in which case only the matching items are kept).
export function applyStatusFilter (plan, currentFilter) {
  if (!plan || !currentFilter) return plan

  const filteredCategories = (plan.data ?? [])
    .map(cat => {
      if (nodeMatchesFilter(cat, currentFilter)) return cat
      const matchingItems = (cat.data ?? []).filter(item => nodeMatchesFilter(item, currentFilter))
      if (matchingItems.length === 0) return null
      return { ...cat, data: matchingItems }
    })
    .filter(Boolean)

  return { ...plan, data: filteredCategories }
}

// True when the active filter has at least one matching item under the
// category, regardless of the category's own status. Drives auto-expand.
export function categoryHasMatchingItem (category, currentFilter) {
  if (!currentFilter || !category) return false
  return (category.data ?? []).some(item => nodeMatchesFilter(item, currentFilter))
}

// Locate any node by id. Returns `{ kind: 'category' | 'item', node, parent }`
// or null. Used by the side panel to render either a selected category or item.
export function findNode (plan, nodeId) {
  if (!plan || !nodeId) return null
  for (const cat of plan.data ?? []) {
    if (cat.id === nodeId) {
      return { kind: 'category', node: cat, parent: plan }
    }
    for (const item of cat.data ?? []) {
      if (item.id === nodeId) {
        return { kind: 'item', node: item, parent: cat }
      }
    }
  }
  return null
}

export function formatPlanDate (iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
}

// Detects whether a description string should be rendered as HTML or Markdown.
// Salesforce-sourced descriptions can come back as raw HTML (`<p>...</p>`) or
// as HTML-entity-encoded HTML (`&lt;p&gt;...&lt;/p&gt;`). Either form is
// treated as HTML so Markup can sanitize/unescape it via dompurify; anything
// else falls through to Markdown so legacy `**bold**` etc still works.
export function detectMarkupType (value) {
  if (!value || typeof value !== 'string') return 'plain'
  if (/<[a-z][\s\S]*>/i.test(value)) return 'html'
  if (/&lt;[a-z]/i.test(value)) return 'html'
  return 'markdown'
}

// Returns the task ids attached to a node (plan / category / item). Prefers
// the explicit `taskIds` array; falls back to extracting ids from a `tasks`
// array if present. Returns null when the node has no linkage at all.
export function resolveItemTaskIds (item) {
  if (!item) return null
  if (Array.isArray(item.taskIds)) {
    return item.taskIds.filter(id => id != null).map(String)
  }
  if (Array.isArray(item.tasks)) {
    return item.tasks
      .map(t => t?.id)
      .filter(id => id != null)
      .map(String)
  }
  return null
}
